import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gift, Copy, Send, TrendingUp, Users, CheckCircle2 } from "lucide-react";

const STATUS_COLORS = {
  invited: "bg-blue-100 text-blue-700",
  signed_up: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  expired: "bg-slate-200 text-slate-700",
};

export default function Referrals() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => setData((await api.get("/referrals/mine")).data), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.includes("@")) return toast.error("Valid email required");
    setBusy(true);
    try {
      await api.post("/referrals/invite", { email: email.trim().toLowerCase(), note });
      toast.success(`Invite sent to ${email}`);
      setEmail(""); setNote(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!data?.share_url) return;
    navigator.clipboard?.writeText(data.share_url);
    toast.success("Share link copied");
  };

  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const Stat = ({ icon: Icon, label, value, tone }) => (
    <div className={`p-4 rounded-sm border ${tone}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-2xl font-bold">{value}</span>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest mt-2 opacity-70">{label}</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="referrals-page">
      {/* Hero */}
      <Card className="p-5 rounded-sm border-accent/40 bg-accent/5">
        <div className="flex items-start gap-4">
          <Gift className="w-8 h-8 text-accent shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Referral rewards</div>
            <div className="font-heading font-bold text-2xl mt-1">Refer a business, earn 1 month free</div>
            <div className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Invite another company to Zenith 360. When they subscribe to any paid plan, we credit
              <b className="text-foreground"> 1 free month</b> of your current plan to your next invoice.
              Stacks — refer 12 businesses, and get a full year on the house.
            </div>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Invites sent" value={data.stats.invited + data.stats.signed_up + data.stats.converted} tone="border-slate-200" />
        <Stat icon={TrendingUp} label="Signed up" value={data.stats.signed_up + data.stats.converted} tone="border-amber-200 bg-amber-50/50" />
        <Stat icon={CheckCircle2} label="Converted" value={data.stats.converted} tone="border-emerald-200 bg-emerald-50/50" />
        <Stat icon={Gift} label="Free months earned" value={data.credits_earned} tone="border-accent/30 bg-accent/5 text-accent" />
      </div>

      {/* Share link + invite form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 rounded-sm">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Your unique link</div>
          <div className="text-xs text-muted-foreground mb-3">Share anywhere — WhatsApp, LinkedIn, email. Anyone who signs up through it counts as yours.</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 border rounded-sm p-2.5 font-mono text-xs bg-muted/30 truncate" data-testid="share-url">{data.share_url}</div>
            <Button onClick={copyLink} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="copy-link">
              <Copy className="w-4 h-4" /> Copy
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground mt-3 font-mono">Your code: <b className="text-foreground text-sm">{data.code}</b></div>
        </Card>

        <Card className="p-5 rounded-sm">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Or invite by email</div>
          <div className="text-xs text-muted-foreground mb-3">We'll send them a personal invite from Zenith 360 with your code baked in.</div>
          <div className="space-y-2">
            <div><Label>Their email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="cfo@theirbusiness.in" className="mt-1" data-testid="invite-email" />
            </div>
            <div><Label>Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hey, thought this would save you hours…" className="mt-1" data-testid="invite-note" />
            </div>
            <Button onClick={invite} disabled={busy || !email} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 mt-2" data-testid="invite-send">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Table */}
      <Card className="rounded-sm">
        <div className="p-4 border-b">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Your referrals</div>
          <div className="font-bold text-lg mt-0.5">{data.referrals.length} total</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/30">
            <tr>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Invited</th>
              <th className="text-left p-3">Converted</th>
              <th className="text-left p-3">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.referrals.map((r) => (
              <tr key={r.id} data-testid={`ref-row-${r.id.slice(0,8)}`}>
                <td className="p-3 font-mono text-xs">{r.invitee_email}</td>
                <td className="p-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[r.status] || STATUS_COLORS.expired}`}>{r.status.replaceAll("_", " ")}</span></td>
                <td className="p-3 text-[11px] text-muted-foreground">{r.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-[11px] text-muted-foreground">{r.converted_at?.slice(0, 10) || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-xs truncate" title={r.invitee_note}>{r.invitee_note || "—"}</td>
              </tr>
            ))}
            {data.referrals.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground italic">
                No referrals yet — grab your share link above and send it to one business you know.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
